// Pure, dependency-free date helpers for the DatePicker.
//
// The picker separates two concepts to stay DST-correct without a date library:
//
//   • Instant  — a real point in time, represented as a JS `Date` (UTC-backed).
//                This is what callers pass in and receive out.
//   • Civil    — a "wall clock" reading (year/month/day/hour/minute/second) with
//                NO zone attached. All calendar/grid math happens on Civil values
//                so navigating months never drifts across DST boundaries.
//
// Conversion between the two happens only at the boundary, via the app's
// existing DST-aware zone engine (see lib/report-timezone). Everything else here
// is plain arithmetic and is trivially unit-testable.

import { zonedDayBoundaryIso } from '@/lib/report-timezone';

export interface Civil {
  year: number;
  month: number; // 1–12
  day: number; // 1–31
  hour: number; // 0–23
  minute: number; // 0–59
  second: number; // 0–59
}

// ── Instant ⇆ Civil (the only zone-aware code path) ──────────────────────────

/** Read an instant as a wall-clock Civil value in `tz`. DST-aware via Intl. */
export function civilFromInstant(date: Date, tz: string): Civil {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

/** Turn a wall-clock Civil value in `tz` into the real instant it denotes.
 *  Reuses the report-timezone boundary converter so DST math stays in one place. */
export function instantFromCivil(c: Civil, tz: string): Date {
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  const iso = `${pad(c.year, 4)}-${pad(c.month)}-${pad(c.day)}T${pad(c.hour)}:${pad(c.minute)}:${pad(c.second)}`;
  // `end=false` keeps the exact seconds we pass (no :59.999 padding).
  const utc = zonedDayBoundaryIso(iso, false, tz);
  return utc ? new Date(utc) : new Date(NaN);
}

// ── Civil arithmetic (pure, zone-free) ───────────────────────────────────────

const startTime = { hour: 0, minute: 0, second: 0 };

export function civil(year: number, month: number, day: number, time: Partial<Civil> = {}): Civil {
  return { year, month, day, ...startTime, hour: time.hour ?? 0, minute: time.minute ?? 0, second: time.second ?? 0 };
}

// ── Form-string ↔ instant bridges ────────────────────────────────────────────
// SupernovaDatePicker exchanges Date instants, but most forms/APIs store dates
// as wall-clock strings ('YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm'). These convert both
// ways through a given timezone. Pass the SAME `tz` to the picker and to both
// converters so the value round-trips without shifting a day (no off-by-one).
// The stored string format is preserved, so existing form/API logic is
// unaffected.

/** 'YYYY-MM-DD' → the instant at 00:00 of that calendar day in `tz`. */
export function civilDateToInstant(value: string | null | undefined, tz: string): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return instantFromCivil({ year: y, month: m, day: d, hour: 0, minute: 0, second: 0 }, tz);
}

/** Instant → 'YYYY-MM-DD' calendar day in `tz` (empty string for null). */
export function instantToCivilDate(date: Date | null, tz: string): string {
  if (!date) return '';
  const c = civilFromInstant(date, tz);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${c.year}-${p(c.month)}-${p(c.day)}`;
}

/** 'YYYY-MM-DDTHH:mm' → the instant at that wall-clock time in `tz`. */
export function civilDateTimeToInstant(value: string | null | undefined, tz: string): Date | null {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = (timePart ?? '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return instantFromCivil({ year: y, month: m, day: d, hour: hh || 0, minute: mm || 0, second: 0 }, tz);
}

/** Instant → 'YYYY-MM-DDTHH:mm' wall-clock in `tz` (empty string for null). */
export function instantToCivilDateTime(date: Date | null, tz: string): string {
  if (!date) return '';
  const c = civilFromInstant(date, tz);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${c.year}-${p(c.month)}-${p(c.day)}T${p(c.hour)}:${p(c.minute)}`;
}

/** Days in a given month, leap-year aware. `month` is 1–12. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Day-of-week for a civil date. 0 = Sunday … 6 = Saturday. */
export function weekday(c: Pick<Civil, 'year' | 'month' | 'day'>): number {
  return new Date(Date.UTC(c.year, c.month - 1, c.day)).getUTCDay();
}

/** Add months to a civil value, clamping the day to the target month length. */
export function addMonths(c: Civil, delta: number): Civil {
  const total = c.year * 12 + (c.month - 1) + delta;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  const day = Math.min(c.day, daysInMonth(year, month));
  return { ...c, year, month, day };
}

export function addYears(c: Civil, delta: number): Civil {
  return addMonths(c, delta * 12);
}

/** Add days to a civil value using UTC epoch math (never crosses a real zone). */
export function addDays(c: Civil, delta: number): Civil {
  const base = new Date(Date.UTC(c.year, c.month - 1, c.day));
  base.setUTCDate(base.getUTCDate() + delta);
  return {
    ...c,
    year: base.getUTCFullYear(),
    month: base.getUTCMonth() + 1,
    day: base.getUTCDate(),
  };
}

/** Serial day number for cheap comparison/diffing of the date portion. */
function serial(c: Pick<Civil, 'year' | 'month' | 'day'>): number {
  return Math.floor(Date.UTC(c.year, c.month - 1, c.day) / 86_400_000);
}

/** Whole-day difference `a - b` (ignores time-of-day). */
export function diffDays(a: Civil, b: Civil): number {
  return serial(a) - serial(b);
}

export function isSameDay(a: Civil, b: Civil): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

export function isSameMonth(a: Civil, b: Civil): boolean {
  return a.year === b.year && a.month === b.month;
}

/** Compare only the date portion: -1 | 0 | 1. */
export function compareDate(a: Civil, b: Civil): number {
  const d = serial(a) - serial(b);
  return d < 0 ? -1 : d > 0 ? 1 : 0;
}

/** Start of the week containing `c`, honoring `firstDayOfWeek` (0–6). */
export function startOfWeek(c: Civil, firstDayOfWeek: number): Civil {
  const wd = weekday(c);
  const back = (wd - firstDayOfWeek + 7) % 7;
  return addDays(c, -back);
}

/** The 1-based calendar quarter (1–4) for a civil date. */
export function quarterOf(c: Civil): number {
  return Math.floor((c.month - 1) / 3) + 1;
}

/** First month (1,4,7,10) of the quarter containing `month`. */
export function quarterStartMonth(month: number): number {
  return Math.floor((month - 1) / 3) * 3 + 1;
}

// ── Month grid ───────────────────────────────────────────────────────────────

export interface GridDay {
  date: Civil;
  inCurrentMonth: boolean;
}

/**
 * Build the 6×7 (42-cell) grid for `year`/`month`, padded with the trailing days
 * of the previous month and leading days of the next so weeks always align to
 * `firstDayOfWeek`. Six rows keeps the popup height stable across months.
 */
export function buildMonthGrid(year: number, month: number, firstDayOfWeek: number): GridDay[] {
  const first = civil(year, month, 1);
  const leading = (weekday(first) - firstDayOfWeek + 7) % 7;
  const gridStart = addDays(first, -leading);
  const cells: GridDay[] = [];
  for (let i = 0; i < 42; i++) {
    const date = addDays(gridStart, i);
    cells.push({ date, inCurrentMonth: date.month === month && date.year === year });
  }
  return cells;
}

/** Ordered weekday indices starting at `firstDayOfWeek`, e.g. [1,2,3,4,5,6,0]. */
export function orderedWeekdays(firstDayOfWeek: number): number[] {
  return Array.from({ length: 7 }, (_, i) => (firstDayOfWeek + i) % 7);
}

// ── Locale names ─────────────────────────────────────────────────────────────

const NAME_ANCHOR = Date.UTC(2021, 0, 3); // a Sunday, for weekday names

export function weekdayNames(locale: string, format: 'short' | 'narrow' | 'long' = 'short'): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: format, timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(NAME_ANCHOR + i * 86_400_000)));
}

export function monthNames(locale: string, format: 'short' | 'long' = 'long'): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { month: format, timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(Date.UTC(2021, i, 15))));
}

// ── Formatting (token → string) ──────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Format a Civil value using a small token language. Supported tokens:
 *   YYYY YY  MMMM MMM MM M  DD D  dddd ddd  HH H  hh h  mm m  ss s  A a
 * Unknown characters pass through literally. This is a deliberately small,
 * predictable subset — no external formatting library needed.
 */
/**
 * Compact, human-readable display for a selected day or range.
 *
 * Exists because `MM/DD/YYYY – MM/DD/YYYY` is 23 characters and gets clipped in
 * a narrow trigger. This drops the redundant halves instead of the text:
 *
 *   single                      → `16 Aug 2026`
 *   today / yesterday           → `Today` / `Yesterday`
 *   range within one month      → `16 - 20 Aug 2026`
 *   range within one year       → `16 Aug - 2 Sep 2026`
 *   range spanning years        → `16 Aug 2025 - 2 Sep 2026`
 *
 * A range whose ends are the same day collapses to the single-date form. One
 * end alone (a half-picked range) formats as that single date.
 *
 * Pass `today` to opt into the relative names. Omit it wherever the caller
 * already says "Today" itself — a preset row labelled "Today" with "Today"
 * underneath it reads as a stutter.
 *
 * Display only: this is not a `formatCivil` token string and nothing parses it
 * back, so use it for read-only triggers and labels.
 */
export function formatCompactRange(
  start: Civil | null,
  end: Civil | null,
  opts: { locale?: string; today?: Civil | null } = {},
): string {
  const { locale = 'en-US', today = null } = opts;

  const single = (c: Civil) => {
    if (today) {
      if (compareDate(c, today) === 0) return 'Today';
      if (compareDate(c, addDays(today, -1)) === 0) return 'Yesterday';
    }
    return formatCivil(c, 'D MMM YYYY', locale);
  };

  if (!start || !end) {
    const only = start ?? end;
    return only ? single(only) : '';
  }
  if (compareDate(start, end) === 0) return single(start);

  // Shared trailing parts are printed once, on the end date, so the eye reads
  // the part that actually varies.
  if (start.year === end.year && start.month === end.month) {
    return `${formatCivil(start, 'D', locale)} - ${formatCivil(end, 'D MMM YYYY', locale)}`;
  }
  if (start.year === end.year) {
    return `${formatCivil(start, 'D MMM', locale)} - ${formatCivil(end, 'D MMM YYYY', locale)}`;
  }
  return `${formatCivil(start, 'D MMM YYYY', locale)} - ${formatCivil(end, 'D MMM YYYY', locale)}`;
}

export function formatCivil(c: Civil, format: string, locale = 'en-US'): string {
  const months = monthNames(locale, 'long');
  const monthsShort = monthNames(locale, 'short');
  const days = weekdayNames(locale, 'long');
  const daysShort = weekdayNames(locale, 'short');
  const wd = weekday(c);
  const h12 = c.hour % 12 === 0 ? 12 : c.hour % 12;
  const isPM = c.hour >= 12;

  const tokens: [string, string][] = [
    ['YYYY', String(c.year).padStart(4, '0')],
    ['YY', String(c.year).slice(-2)],
    ['MMMM', months[c.month - 1]],
    ['MMM', monthsShort[c.month - 1]],
    ['MM', pad2(c.month)],
    ['M', String(c.month)],
    ['DD', pad2(c.day)],
    ['D', String(c.day)],
    ['dddd', days[wd]],
    ['ddd', daysShort[wd]],
    ['HH', pad2(c.hour)],
    ['H', String(c.hour)],
    ['hh', pad2(h12)],
    ['h', String(h12)],
    ['mm', pad2(c.minute)],
    ['m', String(c.minute)],
    ['ss', pad2(c.second)],
    ['s', String(c.second)],
    ['A', isPM ? 'PM' : 'AM'],
    ['a', isPM ? 'pm' : 'am'],
  ];

  // Longest-token-first regex so `MMMM` wins over `MM`, `HH` over `H`, etc.
  const re = new RegExp(tokens.map(([t]) => t).join('|'), 'g');
  const lookup = new Map(tokens);
  return format.replace(re, (m) => lookup.get(m) ?? m);
}

// ── Parsing (string → Civil) ─────────────────────────────────────────────────

/**
 * Best-effort parse of user-typed text against a format. Extracts the numeric
 * date/time components positionally. Returns null when required parts are
 * missing or produce an invalid date. Intentionally lenient about separators so
 * a user typing `1/2/2026` still parses under `MM/DD/YYYY`.
 */
export function parseCivil(input: string, format: string, base: Civil): Civil | null {
  const text = input.trim();
  if (!text) return null;

  const order: ('Y' | 'M' | 'D' | 'h' | 'm' | 's')[] = [];
  const fmtTokens = format.match(/YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|H|hh|h|mm|m|ss|s|A|a/g) ?? [];
  let wants12 = false;
  let wantsMeridiem = false;
  for (const t of fmtTokens) {
    if (t.startsWith('Y')) order.push('Y');
    else if (t.startsWith('M') && t !== 'm' && t !== 'mm') order.push('M');
    else if (t.startsWith('D')) order.push('D');
    else if (t === 'HH' || t === 'H') order.push('h');
    else if (t === 'hh' || t === 'h') {
      order.push('h');
      wants12 = true;
    } else if (t === 'mm' || t === 'm') order.push('m');
    else if (t === 'ss' || t === 's') order.push('s');
    else if (t === 'A' || t === 'a') wantsMeridiem = true;
  }

  const numbers = text.match(/\d+/g)?.map(Number) ?? [];
  if (numbers.length === 0) return null;

  const result: Partial<Civil> = { ...base };
  order.forEach((key, i) => {
    const n = numbers[i];
    if (n == null) return;
    if (key === 'Y') result.year = n < 100 ? 2000 + n : n;
    else if (key === 'M') result.month = n;
    else if (key === 'D') result.day = n;
    else if (key === 'h') result.hour = n;
    else if (key === 'm') result.minute = n;
    else if (key === 's') result.second = n;
  });

  if (wants12 || wantsMeridiem) {
    const pm = /p/i.test(text);
    const am = /a/i.test(text);
    let h = result.hour ?? 0;
    if (pm && h < 12) h += 12;
    if (am && h === 12) h = 0;
    result.hour = h;
  }

  const c: Civil = {
    year: result.year ?? base.year,
    month: result.month ?? base.month,
    day: result.day ?? base.day,
    hour: result.hour ?? base.hour,
    minute: result.minute ?? base.minute,
    second: result.second ?? base.second,
  };

  // Reject impossible values so `13/40/2026` doesn't roll over silently.
  if (c.month < 1 || c.month > 12) return null;
  if (c.day < 1 || c.day > daysInMonth(c.year, c.month)) return null;
  if (c.hour > 23 || c.minute > 59 || c.second > 59) return null;
  return c;
}

/** Convert `HH:mm` (24h) to minutes-since-midnight for time-bound comparison. */
export function hmToMinutes(hm: string | undefined): number | null {
  if (!hm) return null;
  const [h, m] = hm.split(':').map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
}
